import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// Silent auth refresh on 401
// ---------------------------------------------------------------------------
// When the access_token cookie expires the backend returns 401. Instead of
// bouncing the user to the login screen, we transparently call
// /api/auth/refresh (which re-issues the access_token from the still-valid
// refresh_token cookie) and replay the original request.
//
// The refresh_token cookie has a 30-day TTL, so the effective session
// only ends after 30 days of *inactivity*. As long as the user opens the
// app at least once in that window, they stay logged in indefinitely.
// ---------------------------------------------------------------------------
let refreshPromise = null;

function isAuthEndpoint(url = "") {
  return (
    url.includes("/auth/refresh") ||
    url.includes("/auth/login") ||
    url.includes("/auth/logout") ||
    url.includes("/auth/me")
  );
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // HTTP 402 (Payment Required) — trial expired or subscription lapsed.
    // Backend returns { detail, reason, redirect_to } and we auto-redirect
    // the user to the billing tab of Minha Conta so they can pay to
    // unblock access. Skipping the redirect when we're ALREADY on the
    // billing tab avoids infinite loops. Note: ``/billing`` (without
    // ``/minha-conta``) is the ORDER-approval page, not the checkout —
    // never redirect there.
    if (status === 402) {
      const alreadyOnBilling = window.location.pathname.startsWith("/minha-conta")
        && (new URLSearchParams(window.location.search).get("tab") === "billing");
      if (!alreadyOnBilling) {
        const target = error.response?.data?.redirect_to || "/minha-conta?tab=billing";
        setTimeout(() => { window.location.href = target; }, 100);
      }
      return Promise.reject(error);
    }

    // Only try to auto-refresh on 401s that came from a normal API call
    // AND haven't been retried yet AND aren't the refresh call itself.
    if (
      status === 401 &&
      original &&
      !original._retry &&
      !isAuthEndpoint(original.url || "")
    ) {
      original._retry = true;
      try {
        // Dedup concurrent refreshes so N parallel 401s only fire one call.
        if (!refreshPromise) {
          refreshPromise = api
            .post("/auth/refresh")
            .finally(() => {
              refreshPromise = null;
            });
        }
        await refreshPromise;
        return api(original);
      } catch (_) {
        // Refresh itself failed — the refresh_token cookie is dead, so we
        // give up and let the caller handle the 401 (usually redirect to /login).
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Erro inesperado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const MARKETPLACES = [
  { id: "mercado_livre", label: "Mercado Livre", color: "#FFE600", text: "#2D3277" },
  { id: "shopee", label: "Shopee", color: "#EE4D2D", text: "#FFFFFF" },
  { id: "amazon", label: "Amazon", color: "#232F3E", text: "#FF9900" },
  { id: "magalu", label: "Magalu", color: "#0086FF", text: "#FFFFFF" },
  { id: "tiktok_shop", label: "TikTok Shop", color: "#000000", text: "#FE2C55" },
  { id: "shein", label: "Shein", color: "#000000", text: "#FFFFFF" },
];

export const ROLES = [
  { id: "admin", label: "Administrador" },
  { id: "supervisor", label: "Supervisor" },
  { id: "faturamento", label: "Faturamento" },
  { id: "expedicao", label: "Expedição" },
  { id: "consulta", label: "Consulta" },
];

export const STATUS_LABELS = {
  paid: "Pago",
  approved: "Aprovado",
  ready_to_ship: "Pronto para envio",
  awaiting_post: "Aguardando postagem",
  pending_payment: "Aguardando pagamento",
  awaiting_payment: "Pagamento pendente",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
  in_dispute: "Em disputa",
  returned: "Devolvido",
  cancel_requested: "Cancelamento solicitado",
};

export const HUB_STATUS_LABELS = {
  imported: "Importado",
  awaiting_review: "Aguardando conferência",
  approved: "Aprovado",
  rejected: "Rejeitado",
  sent_to_erp: "Enviado ao ERP",
  nf_authorized: "NF autorizada",
  nf_sent: "NF enviada ao marketplace",
  label_ready: "Etiqueta pronta",
  in_separation: "Em separação",
  separated: "Separado",
  packed: "Embalado",
  dispatched: "Despachado",
  blocked: "Bloqueado",
};

export function marketplaceMeta(id) {
  return MARKETPLACES.find((m) => m.id === id) || { id, label: id, color: "#111", text: "#fff" };
}

export function brl(n) {
  return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
